import React, { FunctionComponent, useCallback } from 'react';
import cx from 'classnames';

import { forTemplate } from '@trello/i18n';
import { LazyDescriptionEditor } from '@trello/editor';

import { shouldHandleClick } from 'app/scripts/views/internal/shouldHandleClick';
import { handleLinkClick } from 'app/scripts/views/internal/globalClickHandler';

import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { ChunkLoadErrorBoundary } from 'app/src/components/ChunkLoadErrorBoundary';

import { EditorLoadErrorMessage } from './EditorLoadErrorMessage';

const format = forTemplate('description');

export interface DescriptionProps {
  editable: boolean;
  isAdmin: boolean;
  isCardDesc: boolean;
  placeholderKey: string;
  isTemplate: boolean;
  isEditorEnabled: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editorProps: any;
  onEditorLoadError: () => void;
}

export const Description: FunctionComponent<DescriptionProps> = ({
  editable,
  isCardDesc,
  placeholderKey,
  isAdmin,
  isTemplate,
  isEditorEnabled,
  editorProps,
  onEditorLoadError,
}) => {
  const onClickDescriptionLink = useCallback((e) => {
    const elem = e.target.closest('a');

    if (elem && shouldHandleClick(e)) {
      handleLinkClick(e, elem);
    }
  }, []);

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-aaaa',
        feature: 'Description',
      }}
    >
      <div className="window-module">
        <div className="window-module-title window-module-title-no-divider description-title">
          <span className="icon-description icon-lg window-module-title-icon" />
          <h3 className="u-inline-block">{format('description')}</h3>
          {editable && (
            <div {...{ attr: 'desc' }} className={cx({ editable })}>
              <a
                href="#"
                className="nch-button ml-4 hide-on-edit js-show-with-desc js-edit-desc js-edit-desc-button"
              >
                {format('edit')}
              </a>
              <span className="editing-members-description js-editing-members-description hide" />
            </div>
          )}
        </div>
        <div className="u-gutter">
          <div {...{ attr: 'desc' }} className={cx({ editable })}>
            <div className="description-content js-desc-content">
              <div className="description-content-fade-button">
                <span className="description-content-fade-button-text">
                  {format('show-full-description')}
                </span>
              </div>
              {/* The description may have a child <a> element with a "file://" link. */}
              {/* We want to catch clicks on these as they bubble up, and show a popover. */}
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <div
                dir="auto"
                className="current markeddown hide-on-edit js-desc js-show-with-desc hide"
                onClick={onClickDescriptionLink}
              />
              {!editable && isTemplate && !isCardDesc && !isAdmin && (
                <div className="description-content-no-edit js-hide-with-desc">
                  {format(placeholderKey)}
                </div>
              )}
              {editable && (
                <>
                  <p className="u-bottom js-hide-with-desc">
                    <a
                      href="#"
                      className={cx({
                        'description-fake-text-area hide-on-edit js-edit-desc  js-hide-with-draft': true,
                        large: !isCardDesc,
                      })}
                    >
                      {format(placeholderKey)}
                    </a>
                  </p>
                  <div className="description-edit edit">
                    {isEditorEnabled ? (
                      <>
                        <a
                          href="https://trello.com/b/EXPnqayn"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="helper nch-button js-editor-feedback"
                        >
                          <span className="icon-sm icon-send"></span>
                          {format('editor-feedback')}
                        </a>
                        <div
                          className="js-desc-editor desc-ak-editor"
                          data-test-id="akEditor"
                        >
                          <ChunkLoadErrorBoundary
                            onError={onEditorLoadError}
                            fallback={<EditorLoadErrorMessage />}
                          >
                            <LazyDescriptionEditor {...editorProps} />
                          </ChunkLoadErrorBoundary>
                        </div>
                        <textarea
                          className="field field-autosave js-description-draft description"
                          style={{ display: 'none' }}
                        />
                      </>
                    ) : (
                      <>
                        <a
                          href="#"
                          className="helper nch-button js-format-help"
                        >
                          {format('formatting-help')}
                        </a>
                        <textarea
                          placeholder={format(placeholderKey)}
                          className="field field-autosave js-description-draft description"
                        />
                      </>
                    )}
                  </div>
                  <p className="edits-warning quiet">
                    {format('you-have-unsaved-edits-on-this-field')}
                    {` `}
                    <a href="#" className="js-view-edits">
                      {format('view-edits')}
                    </a>
                    {` - `}
                    <a href="#" className="js-discard-edits">
                      {format('discard')}
                    </a>
                  </p>
                  <p className="edits-error error">
                    {format('edit-not-saved')}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};
